import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { FadeUp, MaskLineScroll } from "../components/Reveal";
import { BRAND, PARTNER_LOGOS } from "../lib/brand";
import { api } from "../lib/api";

const DEFAULTS = [
  { name: "Missile Works", note: "Referral network — engineering, fabrication, motorsport builds." },
  { name: "RevHard", note: "Performance & tuning community relationships." },
  { name: "MoFab", note: "Custom fabrication partners across the Front Range." },
  { name: "Larimer Street Garage", note: "Denver-area collaborations & referrals." },
];

export default function Partners() {
  const [dyn, setDyn] = useState([]);
  useEffect(() => {
    api.get("/partners").then(({ data }) => setDyn(data || [])).catch(() => setDyn([]));
  }, []);

  const list = dyn.length ? dyn : DEFAULTS;

  return (
    <>
      <SEO
        title="Partners — ProForm's Denver Automotive Network"
        description="ProForm collaborates with respected Denver-area shops, importers, tuners, and fabricators. Text-only listings until logos are permitted."
        canonical={`https://${BRAND.domain}/partners`}
      />

      <section className="pt-32 md:pt-40 pb-16 border-b border-ink-700">
        <div className="wrap">
          <div className="label mb-3">— Partners · 001</div>
          <h1 className="font-display uppercase text-5xl md:text-8xl leading-[0.88] tracking-tight max-w-4xl">
            <MaskLineScroll>Trusted across</MaskLineScroll>
            <MaskLineScroll delay={0.08}>Colorado&apos;s <span className="text-proform-red">performance community</span>.</MaskLineScroll>
          </h1>
          <p className="mt-8 text-chrome-300 max-w-2xl text-lg leading-relaxed">
            ProForm collaborates with — and receives referrals through — a network of respected automotive businesses. These are relationships, not sponsorships. Logos are only used with written permission from each partner.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="wrap grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {list.map((p, i) => {
            const logo = p.logo_url || PARTNER_LOGOS[p.name];
            return (
              <FadeUp key={p.name} delay={i * 0.05}>
                <div
                  data-testid={`partner-page-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="card p-8 md:p-10 h-full hover:border-proform-red transition-colors group"
                >
                  <div className="flex items-start justify-between">
                    <div className="label group-hover:text-proform-red transition-colors">— Partner · 0{i + 1}</div>
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noreferrer" className="label hover:text-proform-red">Website ↗</a>
                    )}
                  </div>

                  {logo ? (
                    <div className="mt-8 h-24 md:h-28 flex items-center">
                      <img
                        src={logo}
                        alt={`${p.name} logo`}
                        loading="lazy"
                        className="max-h-full w-auto object-contain opacity-95 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  ) : (
                    <div className="mt-8 h-24 md:h-28 flex items-center">
                      <span className="font-mono text-[10px] tracking-[0.28em] text-chrome-300/60 uppercase">Logo pending</span>
                    </div>
                  )}

                  <div className="font-display uppercase text-3xl md:text-4xl tracking-tight mt-4">{p.name}</div>
                  <p className="mt-4 text-chrome-300 leading-relaxed max-w-md">{p.description || p.note}</p>
                  {p.instagram && (
                    <a href={p.instagram} target="_blank" rel="noreferrer" className="label mt-6 inline-block hover:text-proform-red">Instagram ↗</a>
                  )}
                </div>
              </FadeUp>
            );
          })}
        </div>

        <div className="wrap mt-16 md:mt-24">
          <div className="card p-8 md:p-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-xl">
              <div className="label mb-3">— Collaborate</div>
              <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
                Shop, importer, or builder?
              </h2>
              <p className="mt-4 text-chrome-300">
                ProForm partners with Colorado&apos;s import, tuner, and motorsport network. If you have a client who needs specialty bodywork, get in touch.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/estimate" className="btn btn-primary">Send a project →</Link>
              <a href={`mailto:${BRAND.email}`} className="btn">Email Cody</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
